import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import JSEncrypt from 'jsencrypt';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BankListPseService } from 'src/app/services/bank-list-pse.service';
import { CrmCdtAccountsCustomerService } from 'src/app/services/crm-cdt-accounts-customer.service';
import { GetPublicPassiveKeyService } from 'src/app/services/get-public-passive-key.service';
import { PseReadResumeService } from 'src/app/services/pse-read-resume.service';
import { PseUpdateTransactionService } from 'src/app/services/pse-update-transaction.service';
import { PseService } from 'src/app/services/pse.service';
import { ReadResumeCdtService } from 'src/app/services/read-resume-cdt.service';
import { RefundMoneyService } from 'src/app/services/refund-money.service';
import { SaveResumesCdtService } from 'src/app/services/save-resumes-cdt.service';
import { WebsocketPseService } from 'src/app/services/websocket-pse.service';
import { EnumAccountsType } from 'src/app/shared/enums/accounts-type.enum';
import { EnumPseStatusTransaction } from 'src/app/shared/enums/pse-status-transaction.enum';
import { EnumStepFunctions } from 'src/app/shared/enums/step-functions.enum';
import { EnumTypePerson } from 'src/app/shared/enums/type-person-pse.enum';
import { IPseData } from 'src/app/shared/interfaces/pse-data';
import { IpseSimulationData } from 'src/app/shared/interfaces/pseSimulationData.interface';
import { IRequestRefundMoney } from 'src/app/shared/interfaces/request-refund-money-interface';
import { IReadResumeCdtResponse } from 'src/app/shared/interfaces/responses/read-resume-cdt-response.interface';
import { routesData } from 'src/app/shared/resources/routes.constant';
import { ERROR_REQUIRED_TEXT } from 'src/app/shared/resources/texts.constant';
import { AnalyticService } from 'src/app/shared/services/analytic.service';
import { CustomerDataService } from 'src/app/shared/services/customer-data.service';
import { MoreInfoService } from 'src/app/shared/services/more-info.service';
import { RatesService } from 'src/app/shared/services/rates.service';
import { environment } from 'src/environments/environment';
import { WhyRefundMoneyDialogComponent } from '../dialogs/why-refund-money-dialog/why-refund-money-dialog.component';
import { CdtCalculatorService } from 'src/app/shared/services/cdt-calculator.service';

declare const rsaFunc: any;

@Component({
  selector: 'app-pse',
  templateUrl: './pse.component.html',
  styleUrls: ['./pse.component.scss']
})
export class PseComponent implements OnInit, OnDestroy {
  pseUrl: string = '';
  pseData: IPseData = {} as IPseData;
  pseForm: UntypedFormGroup = {} as UntypedFormGroup;
  simulationData: IpseSimulationData = {} as IpseSimulationData;
  readonly ERROR_REQUIRED_TEXT = ERROR_REQUIRED_TEXT;
  typePersonValues = [
    { text: 'Natural', value: EnumTypePerson.Natural },
    { text: 'Jurídica', value: EnumTypePerson.Legal }
  ];
  accountTypeValues = [
    { text: 'Ahorros', value: EnumAccountsType.Saving },
    { text: 'Corriente', value: EnumAccountsType.Checking }
  ];
  documentTypeValues = [
    { text: 'Cédula ciudadanía', value: 1, prefix: 'CC' },
    { text: 'Cédula extranjería', value: 2, prefix: 'CE' },
    { text: 'NIT', value: 3, prefix: 'NIT' },
    { text: 'Pasaporte', value: 4, prefix: 'PS' },
    { text: 'Registro civil de nacimiento', value: 5, prefix: 'RCN' },
    { text: 'Tarjeta de identidad', value: 6, prefix: 'TI' }
  ];
  banks = [{ bankId: '', name: '' }];
  cdtNumber = '';
  totalCDT: number = 0;
  subscriptions = new Subscription();
  observablesJoin: Observable<HttpResponse<any>>[] = [];
  publicKeyRsa: string = '';
  assetsUrl = environment.assetsUrl;

  get controlBank(): AbstractControl {
    return this.pseForm.get('bank');
  }

  get controlTypePerson(): AbstractControl {
    return this.pseForm.get('typePerson');
  }

  get groupReturnMoney(): AbstractControl {
    return this.pseForm.get('returnMoney');
  }

  get refundAccountType(): AbstractControl {
    return this.pseForm.get('returnMoney.accountType');
  }

  get refundAccountNumber(): AbstractControl {
    return this.pseForm.get('returnMoney.accountNumber');
  }

  get refundBank(): AbstractControl {
    return this.pseForm.get('returnMoney.returnBank');
  }

  get refundDocType(): AbstractControl {
    return this.pseForm.get('returnMoney.documentType');
  }

  get refundDoc(): AbstractControl {
    return this.pseForm.get('returnMoney.documentNumber');
  }

  get prefixDocType(): string {
    const index = this.refundDocType.value - 1;
    return this.documentTypeValues[index].prefix;
  }

  constructor(
    private fb: UntypedFormBuilder,
    public banksList: BankListPseService,
    private data: CustomerDataService,
    private websocket: CrmCdtAccountsCustomerService,
    private router: Router,
    private pseService: PseService,
    private updatePseService: PseUpdateTransactionService,
    public readResumeCdt: ReadResumeCdtService,
    public saveResumeCdt: SaveResumesCdtService,
    public refundMoney: RefundMoneyService,
    private moreInfoService: MoreInfoService,
    public publicKey: GetPublicPassiveKeyService,
    public dialog: MatDialog,
    public breakpointObserver: BreakpointObserver,
    public websocketPseService: WebsocketPseService,
    public analytic: AnalyticService,
    private pseReadResumeService: PseReadResumeService,
    private ratesService: RatesService,
    private cdtCalculatorService: CdtCalculatorService,
    private customerDataService: CustomerDataService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    if (this.data.customerData.stepInUploadDocuments) {
      this.getPublicKey();
    }
    this.retakeCondition();
    this.buildForm();
    this.toggleReturnMoney();
    this.loadPSEInfoFromSimulator();
    this.loadData();
  }

  loadPSEInfoFromSimulator() {
    if (this.moreInfoService.getMoreInfo().retakeCustomerDecision === false) {
      this.simulationData.amount =
        this.data.customerData.cdtSimulationData.amountFormat;
      this.simulationData.days = this.data.customerData.cdtSimulationData.days;
      this.simulationData.rate = this.data.customerData.cdtSimulationData.rate;
    }
  }

  loadPSEInfoFromService() {
    if (this.moreInfoService.getMoreInfo().retakeCustomerDecision === true) {
      this.pseReadResumeService.readPSEResumeCDT().subscribe(
        (d) => {
          if (d && d.status === 200) {
            this.simulationData.amountOriginal = +d.body.cdtAmount;
            this.simulationData.amount = `$  ${Number(
              d.body.cdtAmount
            ).toLocaleString('es-ES', {
              style: 'decimal',
              useGrouping: true,
              maximumFractionDigits: 2
            })}`;

            this.simulationData.days = d.body.cdtTerm;
            this.simulationData.rate = this.ratesService.getRate(
              +d.body.cdtAmount,
              +d.body.cdtTerm
            );
            this.ratesService.simulationDataPse = this.simulationData;
            this.customerDataService.customerData.cdtSimulationData = {
              ...this.customerDataService.customerData.cdtSimulationData,
              days: Number.parseInt(d.body.cdtTerm),
              rate: this.simulationData.rate,
              amountFormat: this.simulationData.amount,
              amount: Number.parseInt(d.body.cdtAmount),
              earningsPeriod: Number.parseInt(d.body.cdtTermination)
            };
          }
        },
        (error) => {
          const errorMessage = error.message ?? '';
          this.analytic.uTagLinkError(
            'Error en el servicio read-summary-transaction-cdt' + errorMessage,
            'error_tech_page'
          );
          this.router.navigate([routesData.ERRORS.GENERAL_ERROR.redirectPath]);
        }
      );
    }
  }

  getPublicKey(): void {
    this.publicKey.getKey().subscribe({
      next: (res) => {
        this.publicKeyRsa = res.body.publicKey;
      },
      error: (error) => {
        let errorMessage = '';
        if (error?.message !== undefined) {
          errorMessage = error.message;
        }
        const event =
          errorMessage === 'Unauthorized'
            ? 'error_user_page'
            : 'error_tech_page';
        this.analytic.uTagLinkError(
          'Error servicio get-passive-public-key ' + errorMessage,
          event
        );
        if (errorMessage !== 'Unauthorized') {
          this.router.navigate([routesData.ERRORS.GENERAL_ERROR.redirectPath]);
        }
      }
    });
  }

  buildForm(): void {
    this.pseForm = this.fb.group({
      bank: [null, Validators.required],
      typePerson: [null, Validators.required],
      returnMoney: this.fb.group({
        returnBank: [null, Validators.required],
        accountType: [this.accountTypeValues[0].value, Validators.required],
        accountNumber: [
          null,
          [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(17)
          ]
        ],
        documentType: [null, Validators.required],
        documentNumber: [
          null,
          [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(10)
          ]
        ]
      })
    });
  }

  loadData(): void {
    this.totalCDT = this.cdtCalculatorService.calculateTotalValueCDT(
      this.data.customerData.cdtSimulationData
    );
  }

  retakeCondition(): void {
    if (
      this.moreInfoService.moreInfoData.retakeCustomerDecision &&
      !this.moreInfoService.moreInfoData.retakeReturnedByPse
    ) {
      this.readRetake();
    } else {
      this.getBanks();
      this.simulationData = this.ratesService.simulationDataPse;
      if (this.moreInfoService.moreInfoData.retakeCustomerDecision) {
        this.cdtNumber = this.moreInfoService.moreInfoData.cdtNumber;
      } else {
        this.subscriptions.add(
          this.websocket
            .observerStepFunctionData$(EnumStepFunctions.StepCreateCdt)
            .subscribe({
              next: (res: any) => {
                this.cdtNumber = res.accountNumber;
              }
            })
        );
      }
    }
  }

  readRetake(): void {
    this.subscriptions.add(
      this.readResumeCdt.read().subscribe({
        next: (res) => {
          if (res?.status === 200) {
            const response: IReadResumeCdtResponse = res.body;
            this.cdtNumber = response.productNumber;
            this.moreInfoService.moreInfoData.originalTransactionId =
              response.originalTransactionId;
            this.loadPSEInfoFromService();
            if (response.state === EnumPseStatusTransaction.ERROR) {
              this.getBanks();
            } else {
              this.updateTransaction(response);
            }
          } else {
            this.analytic.uTagLinkError(
              'Error servicio read-resume-cdt statusCode: ' + res.status,
              'error_pse_page'
            );
            this.router.navigate([
              routesData.ERRORS.PSE_PENDING_ERROR.redirectPath
            ]);
          }
        },
        error: (error) => {
          if (error.status === 401) {
            this.router.navigate([routesData.ERRORS.TIME_ERROR.redirectPath]);
          } else {
            let errorEmptyTable: boolean = false;
            if (error.error) {
              if (error.error.errorCode) {
                if (error.error.errorCode === '001') {
                  errorEmptyTable = true;
                }
              }
            }
            this.emptyTable(errorEmptyTable, error);
          }
        }
      })
    );
  }

  updateTransaction(response: IReadResumeCdtResponse) {
    this.updatePseService
      .updateTransaction({
        originalTransactionId: response.originalTransactionId
      })
      .subscribe({
        next: () => {
          this.moreInfoService.setMoreInfo({
            ...this.moreInfoService.moreInfoData,
            retake: true
          });
          this.router.navigate([routesData.PRODUCT_SUMMARY_PSE.redirectPath], {
            queryParams: {
              tokenws: response.token,
              transactionId: response.originalTransactionId,
              documentNumber: response.documentNumberEncrypted
            }
          });
        },
        error: (error) => {
          const errorMessage = error.message === undefined ? '' : error.message;
          const event =
            errorMessage === 'Unauthorized'
              ? 'error_user_page'
              : 'error_tech_page';
          this.analytic.uTagLinkError(
            'Error servicio pse-update-transaction ' + errorMessage,
            event
          );
          if (errorMessage !== 'Unauthorized') {
            this.router.navigate([
              routesData.ERRORS.GENERAL_ERROR.redirectPath
            ]);
          }
        }
      });
  }

  emptyTable(errorEmptyTable: boolean, error: any) {
    if (errorEmptyTable) {
      const errorMessage = error.error.errorMessage;
      this.analytic.uTagLinkError(
        'Error Retomas ' + errorMessage,
        'error_pse_page'
      );
      this.router.navigate([routesData.ERRORS.PSE_PENDING_ERROR.redirectPath]);
    } else {
      const errorMessage = error.message === undefined ? '' : error.message;
      this.analytic.uTagLinkError(
        'Error servicio read-resume-cdt ' + errorMessage,
        'error_tech_page'
      );
      this.router.navigate([routesData.ERRORS.GENERAL_ERROR.redirectPath]);
    }
  }

  getBanks(): void {
    this.subscriptions.add(
      this.banksList.getBanks().subscribe({
        next: (res) => {
          (res.body as []).shift();
          this.banks = res.body;
        },
        error: (error) => {
          let errorMessage = '';
          if (error?.message !== undefined) {
            errorMessage = error.message;
          }
          const event =
            errorMessage === 'Unauthorized'
              ? 'error_user_page'
              : 'error_pse_page';
          this.analytic.uTagLinkError(
            'Error servicio pse-get-bank-list ' + errorMessage,
            event
          );
          if (errorMessage !== 'Unauthorized') {
            this.router.navigate([routesData.ERRORS.PSE_ERROR.redirectPath]);
          }
        }
      })
    );
  }

  isValidForm(): boolean {
    return this.pseForm.valid;
  }

  saveRefundMoney(): void {
    if (this.data.customerData.stepInUploadDocuments) {
      const encrypt = new JSEncrypt({});
      encrypt.setPublicKey(this.publicKeyRsa);
      const refundDocEncrypt = encrypt.encrypt(this.refundDoc.value).toString();
      const refundDocTypeEncrypt = encrypt
        .encrypt(this.prefixDocType)
        .toString();

      const request: IRequestRefundMoney = {
        refundDoc: refundDocEncrypt,
        refundDocType: refundDocTypeEncrypt,
        accountNumber: this.refundAccountNumber.value,
        accountType: this.refundAccountType.value,
        codeBank: this.refundBank.value,
        requestCode:
          this.data.customerData[
            EnumStepFunctions.StepCreateCdt
          ].requestCode.toString()
      };
      this.observablesJoin.push(
        this.refundMoney.refund(request).pipe(catchError((err) => of(err)))
      );
    }
  }

  saveRetake(): void {
    this.observablesJoin.push(
      this.saveResumeCdt
        .save({
          productNumber: this.cdtNumber,
          returnType: EnumAccountsType.Pse,
          state: EnumPseStatusTransaction.PENDING
        })
        .pipe(catchError((err) => of(err)))
    );
  }

  createTransactionPse(): void {
    if (this.moreInfoService.moreInfoData.retakeReturnedByPse) {
      this.cdtNumber = this.moreInfoService.moreInfoData.cdtNumber;
    }

    this.pseData = {
      bankId: this.controlBank.value,
      fee: this.data.customerData.cdtSimulationData.amount,
      tax: 0,
      userType: this.controlTypePerson.value,
      cdtNumber: this.cdtNumber,
      isRetake: false,
      firstName: this.customerDataService.customerData.firstName,
      lastName: this.customerDataService.customerData.lastName,
      email: this.customerDataService.customerData.contactInformation.email
    };

    if (this.moreInfoService.moreInfoData.retakeCustomerDecision) {
      this.pseData.fee = this.ratesService.simulationDataPse.amountOriginal;
      this.pseData.originalTransactionId =
        this.moreInfoService.moreInfoData.originalTransactionId!;
      this.pseData.isRetake =
        this.moreInfoService.moreInfoData.retakeCustomerDecision!;
      this.observablesJoin.push(
        this.pseService
          .createTransaction(this.pseData)
          .pipe(catchError((err) => of(err)))
      );
    } else {
      this.observablesJoin.push(
        this.pseService
          .createTransaction(this.pseData)
          .pipe(catchError((err) => of(err)))
      );
    }

    this.subscriptions.add(
      forkJoin(this.observablesJoin).subscribe({
        next: (res) => {
          const createTransaction = res[res.length - 1];
          if (createTransaction.ok) {
            this.pseUrl = createTransaction.body.url;
            this.redirect();
          } else {
            this.analytic.uTagLinkError(
              'Error creando transacción pse statusCode: ' +
                res[res.length - 1].status,
              'error_tech_page'
            );
            this.router.navigate([
              routesData.ERRORS.GENERAL_ERROR.redirectPath
            ]);
          }
        },
        error: (error) => {
          let errorMessage = '';
          if (error?.message !== undefined) {
            errorMessage = error.message;
          }
          if (errorMessage !== 'Unauthorized') {
            this.router.navigate([
              routesData.ERRORS.GENERAL_ERROR.redirectPath
            ]);
          }
        }
      })
    );
  }

  redirect(localWindow?: any): void {
    this.moreInfoService.setMoreInfo({
      ...this.moreInfoService.moreInfoData,
      isShowedBackRedirection: true
    });
    const windowObj = localWindow || window;
    windowObj.location.href = this.pseUrl;
  }

  toggleReturnMoney(): void {
    if (this.data.customerData.stepInUploadDocuments) {
      this.groupReturnMoney.enable();
    } else {
      this.groupReturnMoney.disable();
    }
  }

  openDialog(): void {
    this.dialog.open(WhyRefundMoneyDialogComponent, {
      data: '',
      disableClose: true,
      autoFocus: false,
      panelClass: 'dialog-overlay',
      maxWidth: this.breakpointObserver.isMatched('(max-width: 480px)')
        ? '100vw'
        : '480px'
    });
  }

  onSubmit(): void {
    if (this.isValidForm()) {
      this.saveRefundMoney();
      if (!this.moreInfoService.moreInfoData.retakeCustomerDecision) {
        this.saveRetake();
      }
      this.createTransactionPse();
    }
  }
}
